// Classifica.tsx — Wall of Fame: di sempre o del mese, anche per paese. Solo partite valide, solo maggiorenni
// registrati che hanno scelto di comparire (regole sul server).
import { Palco } from "../componenti/Palco.tsx";
import { Avatar } from "../componenti/Avatar.tsx";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAccount } from "../servizi/account.ts";
import { leggiClassifica, PAESI, ultimaClassifica, type RigaClassifica } from "../servizi/social.ts";
import { ScegliPaese } from "../componenti/ScegliPaese.tsx";
import { vai } from "../servizi/percorso.ts";
import { PERCORSO_PROFILO } from "../costanti.ts";
import { leggiDisponibili } from "../servizi/amici.ts";
import { sfidaProfilo } from "../servizi/social.ts";
import { ErroreSfida, IconaSfida, usaSfidaPersona } from "./Persone.tsx";
import { Indietro } from "../componenti/Indietro.tsx";

const PRIMI_DOPO_PODIO = 20;
const DISPONIBILI_MS = 30_000;

export function Classifica({ onIndietro }: { onIndietro: () => void }) {
  const { t, i18n } = useTranslation();
  const account = useAccount();
  const [periodo, setPeriodo] = useState<"sempre" | "mese">("sempre");
  const [paese, setPaese] = useState<string | null>(null);
  const [righe, setRighe] = useState<RigaClassifica[] | null>(null);
  const [errore, setErrore] = useState(false);
  /** v030: dopo il podio si vedono i primi 20; «Vedi tutta la classifica» mostra fino a classifica.righe (100) */
  const [tutta, setTutta] = useState(false);
  const nomiPaesi = useMemo(() => {
    let dn: Intl.DisplayNames | null = null;
    try {
      dn = new Intl.DisplayNames([i18n.language], { type: "region" });
    } catch {
      dn = null;
    }
    return PAESI.map((c) => ({ c, n: dn?.of(c) ?? c })).sort((a, b) => a.n.localeCompare(b.n, i18n.language));
  }, [i18n.language]);
  const nomePaese = (c: string) => nomiPaesi.find((p) => p.c === c)?.n ?? c;

  useEffect(() => {
    let vivo = true;
    setRighe(null);
    setErrore(false);
    setTutta(false);
    leggiClassifica(periodo, paese)
      .then((r) => vivo && setRighe(r))
      .catch(() => vivo && setErrore(true));
    return () => {
      vivo = false;
    };
  }, [periodo, paese]);

  const io = account.tipo === "registrato" ? account.io : null;

  // v033: chi delle righe è online e accetta sfide da chiunque ha il pallino verde e «Sfida» (solo per chi può
  // sfidare sconosciuti: con l'account e maggiorenne). Si ricontrolla ogni DISPONIBILI_MS.
  const [liberi, setLiberi] = useState<ReadonlySet<string>>(new Set());
  const { sfida, invio, errore: erroreSfida } = usaSfidaPersona();
  const puoSfidare = !!io?.puoLink;
  const nomiRighe = (righe ?? []).map((r) => r.nomeUtente).filter((n): n is string => !!n && n !== io?.nomeUtente).join(",");
  useEffect(() => {
    if (!puoSfidare || !nomiRighe) return setLiberi(new Set());
    let vivo = true;
    const leggi = () =>
      leggiDisponibili(nomiRighe.split(","))
        .then((r) => vivo && setLiberi(new Set(r.persone.map((p) => p.nomeUtente ?? ""))))
        .catch(() => undefined);
    void leggi();
    const id = setInterval(() => document.visibilityState === "visible" && void leggi(), DISPONIBILI_MS);
    return () => {
      vivo = false;
      clearInterval(id);
    };
  }, [puoSfidare, nomiRighe]);
  const pulsanteSfida = (nomeUtente: string | null, classe = "") =>
    nomeUtente && liberi.has(nomeUtente) ? (
      <button
        type="button"
        className={`pulsante pulsante-primario pulsante-piccolo ld-sfida-persona ${classe}`}
        disabled={invio !== null}
        aria-label={t("amici.sfida_nome", { nome: nomeUtente })}
        onClick={() => void sfida(`c${nomeUtente}`, (n) => sfidaProfilo(nomeUtente, n))}
      >
        {invio === `c${nomeUtente}` ? "…" : t("amici.sfida")}
        <IconaSfida />
      </button>
    ) : null;

  return (
    <div className="pagina pagina-account ld-pagina">
      <header className="ld-barra">
        <Indietro onClick={onIndietro} />
        <span />
      </header>
      <main className="account-centro classifica">
        <Palco mascotte="caso" colore="oro" titolo={t("social.wall_of_fame")} sottotitolo={t("social.wall_sotto")} />
        <div className="schede-periodo" role="tablist" aria-label={t("social.periodo")}>
          {(["sempre", "mese"] as const).map((p) => (
            <button key={p} type="button" role="tab" aria-selected={periodo === p} className="pulsante pulsante-piccolo" onClick={() => setPeriodo(p)}>
              {t(`social.periodo_${p}`)}
            </button>
          ))}
        </div>
        <ScegliPaese paesi={nomiPaesi} valore={paese} onScegli={setPaese} etichetta={t("social.paese")} nessuno={t("social.tutto_il_mondo")} />
        {errore && <p className="nota" role="alert">{t("account.errore_generico")}</p>}
        {!righe && !errore && <p className="nota" aria-busy="true">{t("generale.caricamento")}</p>}
        {/* v025: i primi tre sul podio (vuoto: tre facce misteriose che aspettano qualcuno) */}
        {righe && (
          <ol className="ld-podio" aria-label={t("social.wall_of_fame")}>
            {[1, 0, 2].map((i) => {
              const r = righe[i];
              return (
                <li key={i} className={`ld-podio-posto ld-podio-${i + 1}`} data-io={!!r && io?.nomeUtente != null && io.nomeUtente === r.nomeUtente}>
                  <span className="ld-persona-avatar">
                    <Avatar quale={r ? (r.avatar ?? "mistero") : "mistero"} dimensione={i === 0 ? 92 : 70} anello={i === 0} classe="ld-podio-avatar" />
                    {r?.nomeUtente && liberi.has(r.nomeUtente) && <span className="ld-pallino" data-online="true" role="img" aria-label={t("amici.online")} />}
                  </span>
                  <span className="ld-podio-nome">{r ? r.nome : "?"}</span>
                  {r && (
                    <span className="ld-podio-punti" role="img" aria-label={t("social.vittorie", { n: r.vinte })}>
                      🏆 {r.vinte}
                    </span>
                  )}
                  {r && pulsanteSfida(r.nomeUtente, "ld-podio-sfida")}
                  <span className="ld-podio-gradino" aria-hidden="true">
                    {i + 1}
                  </span>
                </li>
              );
            })}
          </ol>
        )}
        <ErroreSfida codice={erroreSfida} />
        {righe && righe.length === 0 && <p className="nota">{t("social.classifica_vuota")}</p>}
        {/* v041: la classifica la calcola il server ogni N minuti: lo si dice */}
        {righe && ultimaClassifica.aggiornataIl && (
          <p className="nota-centrata ld-classifica-aggiornata">
            {t("social.classifica_aggiornata", {
              ora: new Intl.DateTimeFormat(i18n.language, { hour: "2-digit", minute: "2-digit" }).format(new Date(ultimaClassifica.aggiornataIl)),
              n: ultimaClassifica.ogniMinuti ?? 15,
            })}
          </p>
        )}
        {righe && righe.length > 3 && (
          <ol className="elenco-classifica" start={4}>
            {righe.slice(3, tutta ? undefined : PRIMI_DOPO_PODIO + 3).map((r, i) => (
              <li
                key={`${r.posizione}-${r.nomeUtente ?? r.nome}`}
                className="ld-riga-classifica"
                style={{ animationDelay: `${Math.min(i, 20) * 35}ms` }}
                data-io={io?.nomeUtente !== null && io?.nomeUtente === r.nomeUtente}
              >
                <span className="posizione">{r.posizione}</span>
                <span className="ld-persona-avatar">
                  <Avatar quale={r.avatar ?? "mistero"} dimensione={40} classe="classifica-avatar" />
                  {r.nomeUtente && liberi.has(r.nomeUtente) && <span className="ld-pallino" data-online="true" role="img" aria-label={t("amici.online")} />}
                </span>
                <span className="chi">
                  <strong>{r.nome}</strong>
                  {r.nomeUtente && (
                    <span className="nota" translate="no">
                      {" "}
                      @{r.nomeUtente}
                    </span>
                  )}
                  {r.paese && <span className="nota"> · {nomePaese(r.paese)}</span>}
                </span>
                {/* v040: il numero con la coppa (la parola «vittorie» in molte lingue rompeva la riga); la parola resta per chi non vede */}
                {pulsanteSfida(r.nomeUtente) ?? (
                  <span className="punti" role="img" aria-label={t("social.vittorie", { n: r.vinte })}>
                    🏆 {r.vinte}
                  </span>
                )}
              </li>
            ))}
          </ol>
        )}
        {righe && !tutta && righe.length > PRIMI_DOPO_PODIO + 3 && (
          <button type="button" className="pulsante pulsante-secondario ld-vedi-tutta" onClick={() => setTutta(true)}>
            {t("social.vedi_tutta", { n: righe.length })}
          </button>
        )}
        <p className="nota-centrata">{t("social.classifica_regole")}</p>
        {io && !io.inClassifica && io.puoClassifica && (
          <button type="button" className="pulsante pulsante-secondario" onClick={() => vai(PERCORSO_PROFILO)}>
            {t("social.compari_anche_tu")}
          </button>
        )}
      </main>
    </div>
  );
}
